/**
 * alljourneys-appointments-seam — the Appointments tab's READ + WRITE path.
 *
 * v2 (2026-09-08). What changed, and why, in the order it bit on a real tenant:
 *
 *  1. HYDRATE. This module provides the door: `loadAppointmentJourney`
 *     (-> `getAdminJourney`) and `designerFromJourney`, which maps the stored
 *     config back onto the designer's own keys. The SCREEN must call them on
 *     mount, merge the result over its defaults, and keep that merged object as
 *     the save baseline — until it does, a Save still writes prototype DEFAULTS
 *     over the real journey (measured on a demo tenant: a fresh browser showed the
 *     opposite of what the server held).
 *  2. DIFF AND SEND. `buildAppointmentPatch(s, ct, { baseline })` emits ONLY the
 *     whitelisted paths whose value differs from the hydrated baseline. A save
 *     that changed one toggle sends one leaf, never the whole settings object,
 *     never `flowOrder` unless the first step moved, never `slug` unless the
 *     operator edited the public link. `movePublicLink: true` rides with a slug
 *     change; the admin PATCH door accepts it from the published-slug guard PR
 *     onward (absent = preserve a published journey's address). The tab has no
 *     slug input today, so no slug is ever sent.
 *  3. TYPED CLIENT. Every call goes through `@v0y4ge/api-client`'s typed
 *     wrappers (`listAdminJourneys`, `getAdminJourney`, `createAdminJourney`,
 *     `updateAdminJourney`, `setAdminJourneyStatus`). The earlier note that the
 *     client lacked them was stale.
 *  4. ADAPTERS. `serviceDisplay` Cards -> `tiles`, Compact -> `list` (reported,
 *     the contract has no compact member); `defaultDate` First-available is the
 *     `firstAvailable` boolean (it was a constant true); the per-attendee field
 *     picker maps to `guestFields`; the attendee roster patch is built from the
 *     STORED roster so `company`/`jobTitle` are never dropped; custom text maps
 *     onto the keys the public wizard actually reads (`steps.*.heading`,
 *     `steps.details.termsText`, `customText['<step>.copy']`) and everything
 *     else is reported through `inertTextKeys()` instead of silently kept.
 *  5. CREATE. `createAppointmentJourney` for a tenant that has none (the
 *     'classic' starter), so the tab is never a dead end.
 *  6. ADDRESS (2026-09-11). One appointment journey per deployment may be
 *     served at the bare booking origin (`https://book.<host>/`), chosen
 *     server-side by `PUBLIC_BOOKING_ROOT_JOURNEY_SLUG`. The admin tab does NOT
 *     ask which one: the admin row itself carries the server's declaration —
 *     `publicUrl` (verbatim, scheme preserved) or `publicPath` against
 *     `domain` — and `publicLinkFor` prints exactly that, else the existing
 *     `scheme://domain/{slug}`. Root-versus-slug is therefore only ever what
 *     those server fields say. The root address is a way IN, never a second
 *     identity: every slug-keyed read and write (published journey, catalogue,
 *     availability, booking) keeps using the internal slug. The PUBLIC mount
 *     resolves the root separately, through the client's `getPublicBookingRoot`
 *     (see public-appointment.tsx); this seam has no root door.
 *
 * Three rules this module keeps (product owner's asks, 2026-09-04):
 *
 *  1. WHITELIST ONLY. Every `steps.*`, `staff.*`, `progress.*` and `advanced.*`
 *     leaf `buildAppointmentPatch` emits appears in EXPOSED below; the only other
 *     keys it can emit are the top-level ones the contract's patch input declares
 *     (`flowOrder`, `layout`, `customText`, `slug`, `movePublicLink`). It never
 *     sends the whole journey object and never the legacy step names `time` /
 *     `confirm` / `staff` under `steps` — staff settings are top-level `staff`.
 *  2. NOTHING IS SWALLOWED. Every refusal comes back to the caller as a typed
 *     `AppointmentSaveRefusal`; there is no `.catch(() => {})` anywhere in this file.
 *  3. A REFUSAL POINTS AT A CONTROL. `422 JOURNEY_CONFIG_INVALID` carries
 *     `fields[].path` (e.g. `steps.datetime.slotInterval`); `controlForPath` maps a
 *     path back to the designer key and the panel that owns it.
 *
 * Deliberately NOT sent, because the contract's whitelist does not carry them and
 * the public wizard has no consumer for them: see UNMAPPED_KEYS. They are reported
 * by `unmappedKeys()` rather than silently dropped. NOT_YET_LIVE lists keys the
 * contract stores but the public page does not render yet (`quickbook`), so the tab
 * can say so instead of implying the toggle changed the visitor's page.
 */
import {
  listAdminJourneys, getAdminJourney, createAdminJourney, updateAdminJourney, setAdminJourneyStatus,
} from "@v0y4ge/api-client";
import { reportOpSource } from "../../shell/opSource";

type Dict = Record<string, unknown>;

/** Transcribed from `JOURNEY_PAGE_SETTINGS_EXPOSED` (contract, main 4a39fa25a).
 *  `steps.datetime.slotInterval` IS consumed by the availability builder but is
 *  not in the contract's group yet; add it here the day the contract PR lands. */
export const EXPOSED: Record<string, readonly string[]> = {
  service: ["enabled", "heading", "displayAs", "showCategories", "showImages", "showDuration", "showPrice", "serviceIds"],
  location: ["enabled", "heading", "mode", "layout", "showNextAvailable", "pinVirtual", "showHours", "showPhone", "branches"],
  // `slotInterval` (2026-09-14, operator): minutes between offered start
  // times, 1..1140, default 60. The note at the top of this file already
  // recorded that the availability builder consumes it but the contract's
  // exposed group does not carry it yet — it is listed here so the tab SENDS
  // it; `unmappedKeys()` reports it until the contract PR lands.
  datetime: ["enabled", "heading", "splitDayparts", "showTimezone", "showCounts", "firstAvailable", "slotInterval"],
  details: ["enabled", "heading", "attendeeFields", "questions", "allowGuests", "maxGuests", "guestFields", "termsText", "requireDocs", "requiredDocs", "notificationPref", "marketingOptIn", "partnerConsent"],
  // `showManage` (Issue 002, 2026-09-15): the appointment confirmation's
  // "Manage this appointment" action. Boolean, default true, backend-owned.
  // `showQr` (CC 0001, 2026-09-18): the reception check-in code. Boolean,
  // default false — the leaf has to be listed here as well as in EXPOSED, or
  // `setStep` drops it on the way out.
  confirmation: ["enabled", "heading", "addToCalendar", "showMap", "showShare", "showPrice", "showManage", "showQr"],
  staff: ["enabled", "showBios", "allowOtherOnPBL", "selected"],
  progress: ["show", "style", "showCount"],
  advanced: ["titlePrefix", "hideJrni", "skipSingleService", "skipSingleLocation"],
};

/** Step groups that live under `steps`; `staff`, `progress`, `advanced` are top-level. */
const STEP_GROUPS = ["service", "location", "datetime", "details", "confirmation"];

/** Designer key -> the control's panel and label, for a refusal that has to point
 *  somewhere. Panel ids are the tab's own section ids. */
export interface ControlRef { key: string; panel: string; label: string }

const CONTROLS: Record<string, ControlRef> = {
  "header.show": { key: "showHeader", panel: "general", label: "Show custom header" },
  "footer.showCustom": { key: "showFooter", panel: "general", label: "Show custom footer" },
  "header.branded": { key: "brandedHeader", panel: "general", label: "Use branded header" },
  "footer.branded": { key: "brandedFooter", panel: "general", label: "Use branded footer" },
  "flowOrder": { key: "firstStep", panel: "general", label: "First step" },
  "layout": { key: "quickbook", panel: "location", label: "Enable Quickbook" },
  "slug": { key: "slug", panel: "launch", label: "Public link" },
  "customText": { key: "ct", panel: "customtext", label: "Custom text" },
  "advanced.skipSingleService": { key: "skipService", panel: "general", label: "Skip the service step when there is one service" },
  "advanced.skipSingleLocation": { key: "skipLocation", panel: "general", label: "Skip the location step when there is one location" },
  "advanced.titlePrefix": { key: "titlePrefix", panel: "general", label: "Browser title prefix" },
  "advanced.hideJrni": { key: "hideJrni", panel: "general", label: "Hide the JRNI footer credit" },
  "staff.enabled": { key: "staffSelect", panel: "general", label: "Let customers choose a specialist" },
  "staff.showBios": { key: "staffBios", panel: "general", label: "Show specialist bios" },
  "staff.allowOtherOnPBL": { key: "staffAllowOther", panel: "general", label: "Allow another specialist on a personal link" },
  "progress.style": { key: "progressType", panel: "progress", label: "Progress bar style" },
  "progress.showCount": { key: "progressType", panel: "progress", label: "Progress bar style" },
  "progress.show": { key: "progressType", panel: "progress", label: "Progress bar style" },
  "steps.service.displayAs": { key: "serviceDisplay", panel: "service", label: "Display of services" },
  "steps.service.showCategories": { key: "showCategories", panel: "service", label: "Show categories" },
  "steps.service.showImages": { key: "showServiceImages", panel: "service", label: "Show service images" },
  "steps.service.showDuration": { key: "showDuration", panel: "service", label: "Show duration" },
  "steps.service.showPrice": { key: "showPrice", panel: "service", label: "Show price" },
  "steps.service.serviceIds": { key: "serviceIds", panel: "service", label: "Services offered on this journey" },
  "steps.service.heading": { key: "ct:service.hdr", panel: "customtext", label: "Service step heading" },
  "steps.location.showNextAvailable": { key: "nextAvailable", panel: "location", label: "Show next available slot per location" },
  "steps.location.showHours": { key: "extraLocationInfo", panel: "location", label: "Show opening hours and phone number" },
  "steps.location.showPhone": { key: "extraLocationInfo", panel: "location", label: "Show opening hours and phone number" },
  "steps.location.heading": { key: "ct:location.hdr", panel: "customtext", label: "Location step heading" },
  "steps.datetime.splitDayparts": { key: "slotGrouping", panel: "datetime", label: "Group slots by part of day" },
  "steps.datetime.showTimezone": { key: "showTimezone", panel: "datetime", label: "Show the time zone" },
  "steps.datetime.showCounts": { key: "showSlotCounts", panel: "datetime", label: "Show how many slots each day has" },
  "steps.datetime.firstAvailable": { key: "defaultDate", panel: "datetime", label: "Which date is selected when the step opens" },
  "steps.datetime.slotInterval": { key: "slotInterval", panel: "datetime", label: "Minutes between start times" },
  "steps.datetime.heading": { key: "ct:datetime.date", panel: "customtext", label: "Date & time step heading" },
  "steps.details.attendeeFields": { key: "askEmail", panel: "details", label: "Contact details asked for (email / phone / address)" },
  "steps.details.allowGuests": { key: "attendees", panel: "details", label: "Additional attendees" },
  "steps.details.maxGuests": { key: "maxParty", panel: "details", label: "Maximum additional attendees" },
  "steps.details.guestFields": { key: "attendeeFields", panel: "details", label: "What you ask for each attendee" },
  "steps.details.notificationPref": { key: "notifPref", panel: "details", label: "Ask for a notification preference" },
  "steps.details.requireDocs": { key: "fileUpload", panel: "details", label: "Document checklist" },
  "steps.details.requiredDocs": { key: "requiredDocs", panel: "details", label: "Documents to bring" },
  "steps.details.marketingOptIn": { key: "marketingOptIn", panel: "details", label: "Offer a marketing opt-in" },
  "steps.details.partnerConsent": { key: "partnerConsent", panel: "details", label: "Require partner consent" },
  "steps.details.termsText": { key: "ct:details.ctext", panel: "customtext", label: "Consent text" },
  "steps.details.heading": { key: "ct:details.hdr", panel: "customtext", label: "Details step heading" },
  "steps.confirmation.addToCalendar": { key: "addToCalendar", panel: "confirmation", label: "Add to calendar" },
  "steps.confirmation.showMap": { key: "showConfMap", panel: "confirmation", label: "Show a map of the location" },
  "steps.confirmation.showShare": { key: "showShare", panel: "confirmation", label: "Show share options" },
  "steps.confirmation.showPrice": { key: "showConfPrice", panel: "confirmation", label: "Show the price" },
  "steps.confirmation.showManage": { key: "showManage", panel: "confirmation", label: "Manage this appointment" },
  /* Mapped 2026-09-18 (CC 0001). It sat in `UNMAPPED_KEYS` — reported, never
     sent — so the toggle flipped, the badge said "Unsaved changes", Save &
     publish said "Published", and the value never left the browser: a save that
     announced success and wrote nothing. Both directions were dead, so a record
     already carrying `showQr: true` still rendered the switch OFF.
     Every sibling on that panel is mapped directly above. */
  "steps.confirmation.showQr": { key: "showQr", panel: "confirmation", label: "QR check-in code" },
  "steps.confirmation.heading": { key: "ct:confirmation.msg", panel: "customtext", label: "Confirmation message" },
};

/** Designer keys with no home in the whitelist AND no consumer on the public
 *  wizard — reported, never sent. */
export const UNMAPPED_KEYS: readonly string[] = [
  "calendarLayout", "showMap", "loadMore", "initialCount", "radius", "radiusUnit", "mapHeight",
  "countryFlags", "customCountries", "maxAttachments", "notifDefault", "readOnly", "takePayment",
  "showStaff", "showResource", "allowReschedule", "allowCancel", "refundMode",
  "reschedule", "errBookStaff", "errOtherServices",
  "embedWidthUnit", "embedWidth", "embedHeight", "embedResize",
];

/** Keys the contract STORES but the public wizard does not render yet. Sent (so the
 *  value survives), but the tab must not imply the visitor's page changed. */
export const NOT_YET_LIVE: readonly string[] = ["quickbook"];

/* ── Custom text: which designer fields reach the public page ───────────── */

/** Custom-text field id -> where the public wizard reads it. Everything else in
 *  the Custom text panel is inert today (the wizard's other strings are fixed per
 *  locale) and is reported through `inertTextKeys()`. */
type TextTarget =
  | { kind: "heading"; step: string }
  | { kind: "terms" }
  | { kind: "copy"; step: string };

/* `location.info` / `datetime.info` / `details.info` are intro-copy fields the
 * Custom text panel does not carry yet (only `service.info` exists); the wizard
 * reads all four `<step>.copy` keys, so the targets are declared for the designer
 * to grow into. */
const TEXT_TARGETS: Record<string, TextTarget> = {
  "service.hdr": { kind: "heading", step: "service" },
  "location.hdr": { kind: "heading", step: "location" },
  "datetime.date": { kind: "heading", step: "datetime" },
  "details.hdr": { kind: "heading", step: "details" },
  "confirmation.msg": { kind: "heading", step: "confirmation" },
  "details.ctext": { kind: "terms" },
  "service.info": { kind: "copy", step: "service" },
  "location.info": { kind: "copy", step: "location" },
  "datetime.info": { kind: "copy", step: "datetime" },
  "details.info": { kind: "copy", step: "details" },
};

/** A `customText` key this designer owns: any Custom-text panel field id
 *  (`<group>.<field>`) or one of the four `<step>.copy` homes. Stored keys outside
 *  that set belong to other surfaces and are carried through untouched. */
function designerOwnsTextKey(key: string): boolean {
  if (TEXT_TARGETS[key]) return true;
  if (/^(service|location|datetime|details)[.]copy$/.test(key)) return true;
  return /^(progress|service|location|datetime|details|confirmation|error|cancel)[.][a-z]+$/.test(key);
}

/** Custom-text ids the operator filled in that no public surface reads. */
export function inertTextKeys(ct: Dict): string[] {
  return Object.keys(ct || {}).filter((k) => {
    const v = ct[k];
    return typeof v === "string" && v.trim() !== "" && !TEXT_TARGETS[k];
  });
}

export function controlForPath(path: string): ControlRef | null {
  if (CONTROLS[path]) return CONTROLS[path];
  // `steps.details.attendeeFields.3.state` -> the array control that owns it.
  const parts = path.split(".");
  while (parts.length > 1) {
    parts.pop();
    const parent = parts.join(".");
    if (CONTROLS[parent]) return CONTROLS[parent];
  }
  return null;
}

/* ── Small helpers ──────────────────────────────────────────────────────── */

function isRecord(v: unknown): v is Dict {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}
function rec(v: unknown): Dict { return isRecord(v) ? v : {}; }
function str(v: unknown, fallback = ""): string { return typeof v === "string" ? v : fallback; }
function bool(v: unknown, fallback: boolean): boolean { return typeof v === "boolean" ? v : fallback; }

/** Minutes between offered start times: the designer's existing bounds 1..1140,
 *  default 60. A finite number is rounded and held to the bounds; anything else
 *  is `null` (not a value this seam will send or keep). */
export const SLOT_INTERVAL_MIN = 1;
export const SLOT_INTERVAL_MAX = 1140;
export const SLOT_INTERVAL_DEFAULT = 60;
export function slotIntervalMinutes(v: unknown): number | null {
  const n = typeof v === "number" ? v : typeof v === "string" && v.trim() !== "" ? Number(v) : NaN;
  if (!Number.isFinite(n)) return null;
  return Math.min(SLOT_INTERVAL_MAX, Math.max(SLOT_INTERVAL_MIN, Math.round(n)));
}

function setStep(patch: Dict, step: string, field: string, value: unknown): void {
  if (STEP_GROUPS.indexOf(step) < 0) return;
  if ((EXPOSED[step] || []).indexOf(field) < 0) return;
  const steps = (patch.steps as Dict) || (patch.steps = {} as Dict);
  const page = (steps[step] as Dict) || (steps[step] = {} as Dict);
  page[field] = value;
}
function setGroup(patch: Dict, group: "staff" | "progress" | "advanced", field: string, value: unknown): void {
  if ((EXPOSED[group] || []).indexOf(field) < 0) return;
  const g = (patch[group] as Dict) || (patch[group] = {} as Dict);
  g[field] = value;
}

/** The contract's closed attendee roster, defaults verbatim from the contract, used
 *  only when the journey carries no roster of its own. */
const ATTENDEE_ROSTER_DEFAULTS: Array<Dict> = [
  { id: "firstName", label: "First name", type: "text", state: "required", locked: true, hint: "Required for every attendee record." },
  { id: "lastName", label: "Last name", type: "text", state: "required", locked: true, hint: "Required for every attendee record." },
  { id: "email", label: "Email", type: "email", state: "required", locked: true, hint: "Required for confirmations and receipts." },
  { id: "phone", label: "Phone", type: "tel", state: "optional", locked: false, hint: "" },
  { id: "company", label: "Company", type: "text", state: "hidden", locked: false, hint: "" },
  { id: "jobTitle", label: "Job title", type: "text", state: "hidden", locked: false, hint: "" },
  { id: "address", label: "Address", type: "text", state: "hidden", locked: false, hint: "" },
];

/** The designer's three ask/optional toggle pairs applied over the STORED roster,
 *  so rows the designer has no toggle for (`company`, `jobTitle`) round-trip intact. */
function attendeeRoster(s: Dict, stored: unknown): Array<Dict> {
  const base = Array.isArray(stored) && stored.length > 0 ? (stored as Array<Dict>) : ATTENDEE_ROSTER_DEFAULTS;
  const state = (on: unknown, optional: unknown) => (on ? (optional ? "optional" : "required") : "hidden");
  const wanted: Record<string, string> = {
    email: state(s.askEmail, s.emailOptional),
    phone: state(s.askPhone, s.phoneOptional),
    address: state(s.askAddress, s.addressOptional),
  };
  return base.map((row) => {
    const id = str(row.id);
    return wanted[id] ? { ...row, state: wanted[id] } : { ...row };
  });
}

/** Designer per-attendee picker (first/last/email/phone) <-> contract `guestFields`. */
function guestFieldsFrom(picked: unknown): "count" | "name" | "full" {
  const list = Array.isArray(picked) ? picked.map(String) : [];
  if (list.length === 0) return "count";
  if (list.indexOf("email") >= 0 || list.indexOf("phone") >= 0) return "full";
  return "name";
}
function pickedFromGuestFields(mode: unknown): string[] {
  if (mode === "full") return ["first", "last", "email", "phone"];
  if (mode === "count") return [];
  return ["first", "last"];
}

/* ── Designer -> contract ───────────────────────────────────────────────── */

export interface PatchOptions {
  /** The designer state the tab was hydrated with (or last saved). When given,
   *  the patch carries ONLY leaves that differ from it. */
  baseline?: { s: Dict; ct: Dict } | null;
  /** The stored journey (from `loadAppointmentJourney`), used for the attendee
   *  roster so rows without a designer toggle are preserved. */
  journey?: Dict | null;
}

/** The designer's settings object -> a FULL candidate patch in contract names. */
function fullPatch(s: Dict, ct: Dict, journey: Dict | null | undefined): Dict {
  const patch: Dict = {};
  const storedSteps = rec(rec(journey).steps);
  if (typeof s.showHeader === "boolean") patch.header = { show: s.showHeader };
  if (typeof s.showFooter === "boolean") patch.footer = { showCustom: s.showFooter };
  // Branded chrome (2026-09-23): sent only when the designer holds a value.
  if (typeof s.brandedHeader === "boolean") patch.header = { ...(patch.header as Dict || {}), branded: s.brandedHeader };
  if (typeof s.brandedFooter === "boolean") patch.footer = { ...(patch.footer as Dict || {}), branded: s.brandedFooter };

  // General / flow
  const first = String(s.firstStep || "service");
  patch.flowOrder = first === "location" ? "location-first" : first === "map" ? "map-first" : "service-first";
  // Quickbook is the journey LAYOUT, top level — not a location-page field.
  patch.layout = s.quickbook ? "quickbook" : "steps";
  setGroup(patch, "advanced", "skipSingleService", !!s.skipService);
  setGroup(patch, "advanced", "skipSingleLocation", !!s.skipLocation);
  if (typeof s.titlePrefix === "string") setGroup(patch, "advanced", "titlePrefix", s.titlePrefix);
  if (typeof s.hideJrni === "boolean") setGroup(patch, "advanced", "hideJrni", s.hideJrni);
  setGroup(patch, "staff", "enabled", !!s.staffSelect);
  if (typeof s.staffBios === "boolean") setGroup(patch, "staff", "showBios", s.staffBios);
  if (typeof s.staffAllowOther === "boolean") setGroup(patch, "staff", "allowOtherOnPBL", s.staffAllowOther);

  // Progress bar: `numbers` is the tab's word for the contract's `segments` WITH
  // the step count shown, so SWITCHING to it from another style turns the count
  // on. The designer has no separate count control, so in every other case the
  // stored value is carried as-is — a hydrated journey must map back leaf for
  // leaf (a stored "segments without count" stays exactly that).
  const pt = String(s.progressType || "titles");
  const storedProgress = rec(rec(journey).progress);
  setGroup(patch, "progress", "show", pt !== "none");
  // "none" hides the bar and says nothing about its style: carry the stored one.
  const storedStyle = str(storedProgress.style);
  const style = pt === "numbers" ? "segments" : pt === "bar" ? "bar" : pt === "titles" ? "titles"
    : (storedStyle === "bar" || storedStyle === "segments" || storedStyle === "titles" ? storedStyle : "titles");
  setGroup(patch, "progress", "style", style);
  if (pt === "numbers" && storedProgress.style !== "segments") setGroup(patch, "progress", "showCount", true);
  else if (typeof storedProgress.showCount === "boolean") setGroup(patch, "progress", "showCount", storedProgress.showCount);

  // Service page. Cards -> tiles; List and Compact -> list (the contract has no
  // compact member; `unmappedKeys` reports the choice through `serviceDisplay`).
  setStep(patch, "service", "displayAs", s.serviceDisplay === "grid" || s.serviceDisplay === "tiles" ? "tiles" : "list");
  setStep(patch, "service", "showCategories", !!s.showCategories);
  setStep(patch, "service", "showImages", !!s.showServiceImages);
  setStep(patch, "service", "showDuration", !!s.showDuration);
  setStep(patch, "service", "showPrice", !!s.showPrice);
  if (Array.isArray(s.serviceIds)) setStep(patch, "service", "serviceIds", s.serviceIds.map(String));

  // Location page. One designer toggle drives two stored flags; when the toggle
  // agrees with the stored pair's OR, both flags are left exactly as stored so an
  // asymmetric pair (hours on, phone off) is never rewritten by an untouched save.
  setStep(patch, "location", "showNextAvailable", !!s.nextAvailable);
  const storedLoc = rec(storedSteps.location);
  const storedExtra = bool(storedLoc.showHours, false) || bool(storedLoc.showPhone, false);
  if (!!s.extraLocationInfo !== storedExtra || !("showHours" in storedLoc)) {
    setStep(patch, "location", "showHours", !!s.extraLocationInfo);
    setStep(patch, "location", "showPhone", !!s.extraLocationInfo);
  } else {
    setStep(patch, "location", "showHours", bool(storedLoc.showHours, false));
    setStep(patch, "location", "showPhone", bool(storedLoc.showPhone, false));
  }

  // Date & time page
  setStep(patch, "datetime", "splitDayparts", s.slotGrouping === "partofday");
  setStep(patch, "datetime", "showTimezone", !!s.showTimezone);
  if (typeof s.showSlotCounts === "boolean") setStep(patch, "datetime", "showCounts", s.showSlotCounts);
  // "First date with availability" is the shortcut link; "Today" turns it off.
  setStep(patch, "datetime", "firstAvailable", s.defaultDate === "firstAvailable");
  // Minutes between offered start times. Sent only when the designer holds a
  // usable number; the diff below then carries it only when it changed.
  const interval = slotIntervalMinutes(s.slotInterval);
  if (interval !== null) setStep(patch, "datetime", "slotInterval", interval);

  // Details page
  setStep(patch, "details", "attendeeFields", attendeeRoster(s, rec(storedSteps.details).attendeeFields));
  setStep(patch, "details", "allowGuests", !!s.attendees);
  if (typeof s.maxParty === "number") setStep(patch, "details", "maxGuests", Math.min(Math.max(Math.round(s.maxParty), 1), 20));
  setStep(patch, "details", "guestFields", guestFieldsFrom(s.attendeeFields));
  setStep(patch, "details", "notificationPref", !!s.notifPref);
  setStep(patch, "details", "requireDocs", !!s.fileUpload);
  if (Array.isArray(s.requiredDocs)) setStep(patch, "details", "requiredDocs", s.requiredDocs.map(String).filter((d) => d.trim() !== ""));
  if (typeof s.marketingOptIn === "boolean") setStep(patch, "details", "marketingOptIn", s.marketingOptIn);
  if (typeof s.partnerConsent === "boolean") setStep(patch, "details", "partnerConsent", s.partnerConsent);

  // Confirmation page
  setStep(patch, "confirmation", "addToCalendar", !!s.addToCalendar);
  if (typeof s.showConfMap === "boolean") setStep(patch, "confirmation", "showMap", s.showConfMap);
  if (typeof s.showShare === "boolean") setStep(patch, "confirmation", "showShare", s.showShare);
  if (typeof s.showConfPrice === "boolean") setStep(patch, "confirmation", "showPrice", s.showConfPrice);
  if (typeof s.showManage === "boolean") setStep(patch, "confirmation", "showManage", s.showManage);
  if (typeof s.showQr === "boolean") setStep(patch, "confirmation", "showQr", s.showQr);

  // Custom text. Headings and the consent text live on their step (an empty
  // field means "platform default" and is not sent — the server refuses an empty
  // heading). Intro copy lives in the typed `customText` record under the
  // `<step>.copy` keys the wizard looks up; EVERY other filled-in field is kept
  // in `customText` under its own id so nothing the operator typed is lost
  // (`inertTextKeys()` reports which of those the public page does not read yet).
  // The server REPLACES `customText` wholesale, so the record is rebuilt in full:
  // stored keys this designer does not own are carried, designer keys are
  // rewritten, and a cleared field is a deletion.
  const copy: Record<string, string> = {};
  const storedCopy = rec(rec(journey).customText);
  Object.keys(storedCopy).forEach((k) => {
    const v = storedCopy[k];
    if (typeof v === "string" && !designerOwnsTextKey(k)) copy[k] = v;
  });
  Object.keys(ct || {}).forEach((k) => {
    const v = ct[k];
    if (typeof v !== "string" || v.trim() === "") return;
    const target = TEXT_TARGETS[k];
    if (!target) { copy[k] = v; return; }
    if (target.kind === "heading") { setStep(patch, target.step, "heading", v); return; }
    if (target.kind === "terms") { setStep(patch, "details", "termsText", v); return; }
    copy[target.step + ".copy"] = v;
  });
  if (Object.keys(copy).length || Object.keys(storedCopy).length) patch.customText = copy;

  if (typeof s.slug === "string" && s.slug.trim() !== "") patch.slug = s.slug.trim();
  return patch;
}

/** Keep only the leaves of `next` whose value differs from `prev`. Arrays and
 *  primitives compare by value; empty objects are pruned. */
function diffLeaves(next: Dict, prev: Dict): Dict {
  const out: Dict = {};
  Object.keys(next).forEach((k) => {
    const n = next[k]; const p = prev[k];
    if (isRecord(n) && isRecord(p)) {
      const inner = diffLeaves(n, p);
      if (Object.keys(inner).length) out[k] = inner;
      return;
    }
    if (JSON.stringify(n) !== JSON.stringify(p)) out[k] = n;
  });
  return out;
}

/**
 * The designer's settings object -> an `updateAdminJourney` patch containing ONLY
 * whitelisted fields, in their runtime names — and, when a baseline is given, only
 * the ones that changed. `customText` is REPLACED wholesale by the server, so when
 * any copy key changed the whole copy record is sent, never a slice.
 */
export function buildAppointmentPatch(s: Dict, ct: Dict = {}, opts: PatchOptions = {}): Dict {
  const next = fullPatch(s, ct, opts.journey);
  if (!opts.baseline) return next;
  const prev = fullPatch(opts.baseline.s, opts.baseline.ct, opts.journey);
  const patch = diffLeaves(next, prev);
  // customText is a replacement record: deleting a key is a change even when
  // every remaining key is identical. Recursive leaf diffs cannot detect that.
  if (JSON.stringify(next.customText) !== JSON.stringify(prev.customText)) {
    patch.customText = next.customText ?? {};
  }
  if (patch.slug !== undefined) patch.movePublicLink = true;
  return patch;
}

/** Every designer key this save could not carry, so the tab can say so out loud.
 *  With a baseline, only the keys the operator actually CHANGED are listed —
 *  without one, every unmapped key present in `s` is (DEFAULTS make that ~28). */
export function unmappedKeys(s: Dict, baselineS?: Dict | null): string[] {
  const keys = UNMAPPED_KEYS.filter((k) => {
    if (!Object.prototype.hasOwnProperty.call(s, k)) return false;
    return baselineS ? JSON.stringify(s[k]) !== JSON.stringify(baselineS[k]) : true;
  });
  if (s.serviceDisplay === "compact" && (!baselineS || baselineS.serviceDisplay !== "compact")) keys.push("serviceDisplay");
  return keys;
}

/** Designer keys whose value is stored but not yet rendered on the public page. */
export function notYetLiveKeys(s: Dict): string[] {
  return NOT_YET_LIVE.filter((k) => Object.prototype.hasOwnProperty.call(s, k) && !!s[k]);
}

/* ── Contract -> designer ───────────────────────────────────────────────── */

/**
 * The stored journey -> the designer keys it determines. Returns ONLY the keys
 * the journey carries, so the caller merges the result over its defaults:
 * `{ ...DEFAULTS, ...designerFromJourney(journey).s }`.
 */
export function designerFromJourney(journey: Dict): { s: Dict; ct: Record<string, string> } {
  const s: Dict = {};
  const ct: Record<string, string> = {};
  s.showHeader = rec(journey.header).show !== false;
  s.showFooter = rec(journey.footer).showCustom !== false;
  s.brandedHeader = rec(journey.header).branded === true;
  s.brandedFooter = rec(journey.footer).branded === true;
  const steps = rec(journey.steps);
  const service = rec(steps.service); const location = rec(steps.location);
  const datetime = rec(steps.datetime); const details = rec(steps.details);
  const confirmation = rec(steps.confirmation);
  const staff = rec(journey.staff); const progress = rec(journey.progress); const advanced = rec(journey.advanced);

  // NOTE: the admin read derives `flowOrder` from `order[0]` and can only report
  // service-first / location-first; a stored map-first journey reads back as
  // location-first until the contract stores the flow explicitly. Sending
  // `map-first` still applies the map-specific step patch server-side.
  const flow = str(journey.flowOrder) || (Array.isArray(journey.order) && journey.order[0] === "location" ? "location-first" : "");
  if (flow) s.firstStep = flow === "location-first" ? "location" : flow === "map-first" ? "map" : "service";
  if (typeof journey.layout === "string") s.quickbook = journey.layout === "quickbook";
  if ("skipSingleService" in advanced) s.skipService = bool(advanced.skipSingleService, true);
  if ("skipSingleLocation" in advanced) s.skipLocation = bool(advanced.skipSingleLocation, true);
  if (typeof advanced.titlePrefix === "string") s.titlePrefix = advanced.titlePrefix;
  if (typeof advanced.hideJrni === "boolean") s.hideJrni = advanced.hideJrni;
  if ("enabled" in staff) s.staffSelect = bool(staff.enabled, false);
  if (typeof staff.showBios === "boolean") s.staffBios = staff.showBios;
  if (typeof staff.allowOtherOnPBL === "boolean") s.staffAllowOther = staff.allowOtherOnPBL;

  if ("show" in progress || "style" in progress) {
    const show = bool(progress.show, true); const style = str(progress.style, "titles");
    s.progressType = !show ? "none" : style === "segments" ? "numbers" : style === "bar" ? "bar" : "titles";
  }

  if ("displayAs" in service) s.serviceDisplay = service.displayAs === "tiles" ? "grid" : "list";
  if ("showCategories" in service) s.showCategories = bool(service.showCategories, true);
  if ("showImages" in service) s.showServiceImages = bool(service.showImages, true);
  if ("showDuration" in service) s.showDuration = bool(service.showDuration, true);
  if ("showPrice" in service) s.showPrice = bool(service.showPrice, true);
  if (Array.isArray(service.serviceIds)) s.serviceIds = service.serviceIds.map(String);

  if ("showNextAvailable" in location) s.nextAvailable = bool(location.showNextAvailable, true);
  if ("showHours" in location || "showPhone" in location) s.extraLocationInfo = bool(location.showHours, false) || bool(location.showPhone, false);

  if ("splitDayparts" in datetime) s.slotGrouping = bool(datetime.splitDayparts, true) ? "partofday" : "flat";
  if ("showTimezone" in datetime) s.showTimezone = bool(datetime.showTimezone, true);
  if ("showCounts" in datetime) s.showSlotCounts = bool(datetime.showCounts, true);
  if ("firstAvailable" in datetime) s.defaultDate = bool(datetime.firstAvailable, true) ? "firstAvailable" : "today";
  // A stored interval is what the tab shows (a saved 30 reads as 30, not the
  // default 60). Absent leaves the designer default; unusable reads as 60.
  if ("slotInterval" in datetime) s.slotInterval = slotIntervalMinutes(datetime.slotInterval) ?? SLOT_INTERVAL_DEFAULT;

  const roster = Array.isArray(details.attendeeFields) ? (details.attendeeFields as Array<Dict>) : [];
  roster.forEach((row) => {
    const id = str(row.id); const state = str(row.state);
    if (id === "email") { s.askEmail = state !== "hidden"; s.emailOptional = state === "optional"; }
    if (id === "phone") { s.askPhone = state !== "hidden"; s.phoneOptional = state === "optional"; }
    if (id === "address") { s.askAddress = state !== "hidden"; s.addressOptional = state === "optional"; }
  });
  if ("allowGuests" in details) s.attendees = bool(details.allowGuests, false);
  if (typeof details.maxGuests === "number") s.maxParty = details.maxGuests;
  if ("guestFields" in details) s.attendeeFields = pickedFromGuestFields(details.guestFields);
  if ("notificationPref" in details) s.notifPref = bool(details.notificationPref, true);
  if ("requireDocs" in details) s.fileUpload = bool(details.requireDocs, false);
  if (Array.isArray(details.requiredDocs)) s.requiredDocs = details.requiredDocs.map(String);
  if (typeof details.marketingOptIn === "boolean") s.marketingOptIn = details.marketingOptIn;
  if (typeof details.partnerConsent === "boolean") s.partnerConsent = details.partnerConsent;

  if ("addToCalendar" in confirmation) s.addToCalendar = bool(confirmation.addToCalendar, true);
  if (typeof confirmation.showMap === "boolean") s.showConfMap = confirmation.showMap;
  if (typeof confirmation.showShare === "boolean") s.showShare = confirmation.showShare;
  if (typeof confirmation.showPrice === "boolean") s.showConfPrice = confirmation.showPrice;
  // Absent or non-boolean keeps the designer default (true); only a stored
  // `false` hides the manage action.
  if ("showManage" in confirmation) s.showManage = bool(confirmation.showManage, true);
  /* Read back, so a stored setting wins over the designer default. Without
     this the switch rendered OFF on a record that carried `showQr: true` — the
     read half of CC 0001, and the half a whitelist entry alone does not fix. */
  if ("showQr" in confirmation) s.showQr = bool(confirmation.showQr, false);

  if (typeof journey.slug === "string" && journey.slug) s.slug = journey.slug;

  // Custom text: the typed homes first, then every stored `customText` key that
  // is a designer field id (the `<step>.copy` keys are the copy targets' homes).
  const stored = rec(journey.customText);
  Object.keys(stored).forEach((k) => {
    const v = stored[k];
    if (typeof v === "string" && v !== "" && !/\.copy$/.test(k)) ct[k] = v;
  });
  Object.keys(TEXT_TARGETS).forEach((id) => {
    const target = TEXT_TARGETS[id];
    let v: unknown;
    if (target.kind === "heading") v = rec(steps[target.step]).heading;
    else if (target.kind === "terms") v = details.termsText;
    else v = rec(journey.customText)[target.step + ".copy"];
    if (typeof v === "string" && v !== "") ct[id] = v;
  });

  return { s, ct };
}

/* ── The customer's manage link (Issue 002) ─────────────────────────────── */

/** Which confirmation a journey renders. `appointment`, `events` and `landing`
 *  can carry the manage action; queue and kiosk confirmations never do. */
export type ConfirmationKind = "appointment" | "events" | "landing" | "queue" | "kiosk";

/**
 * The address behind the confirmation's "Manage" action, or "" when the action
 * must not render.
 *
 * Reads ONLY a server-issued manage link off the public response the wizard
 * holds (`st.booking`) — `booking.manage_url` for an appointment, and for an
 * EVENT registration the `manageUrl` the api-client forwards off the worker's
 * `data.manage_url` (CC, 2026-09-22). It is never built from
 * `orderRef`, a booking id or any other field — the management credential is
 * the server's to issue, so a response without it has no manage action. The
 * value must be an absolute http(s) URL with a host and no userinfo; it is
 * returned exactly as issued (surrounding whitespace aside).
 *
 * An event legitimately arrives without one — a duplicate submit answers 200
 * without it, and a tenant that publishes no manage surface never sends one —
 * so an absent link hides the button rather than producing a dead one.
 *
 * `showManage` is the authored setting: only an explicit `false` hides it, so
 * an unset value behaves as the default (true).
 */
export function appointmentManageUrl(response: unknown, showManage: unknown, kind: ConfirmationKind): string {
  /* `landing` counts (CC, 2026-09-23). It is not a second surface — it is where
     every REAL event registration finishes: `public-events.tsx` renders
     `journey={route.eventId ? "landing" : "events"}`, so "events" is only the
     list, and picking an event puts its id in the route. Your info and
     Confirmation are both on the landing kind. Admitting "events" alone meant
     the action could never render for a customer, which is what CC measured
     live: the button was in the served chunk and never drew. */
  if (kind !== "appointment" && kind !== "events" && kind !== "landing") return "";
  if (showManage === false) return "";
  const root = rec(response);
  /* An appointment reads its OWN nested field and nothing else. The root read
     is scoped to events, because an appointment response is not supposed to
     carry one and honouring it there would hand back a link the appointment
     rules never sanctioned — which is what `appointment-manage-action.test.ts`
     asserts and what my first cut broke (CC, 2026-09-22). */
  if (kind === "appointment") return safeManageUrl(rec(root.booking).manage_url);
  /* Event AND landing: the api-client's camelCase forward off
     `data.manage_url`, with the worker's snake_case accepted beside it so a
     response read straight off the wire behaves the same. */
  return safeManageUrl(root.manageUrl) || safeManageUrl(root.manage_url);
}

/**
 * A server-issued manage link as it may be opened, or "" when it is not one:
 * an absolute http(s) URL with a host and no userinfo, returned exactly as
 * issued (surrounding whitespace aside). The appointment action and each event
 * attendee's Manage link both go through it, so neither can open a
 * `javascript:` or credential-bearing address.
 */
export function safeManageUrl(raw: unknown): string {
  if (typeof raw !== "string") return "";
  const value = raw.trim();
  if (!/^https?:\/\/[^\s]+$/i.test(value)) return "";
  try {
    const url = new URL(value);
    if (url.protocol !== "http:" && url.protocol !== "https:") return "";
    if (!url.hostname || url.username || url.password) return "";
  } catch (err) {
    return "";
  }
  return value;
}

/** How the confirmation's "Manage this appointment" action renders.
 *  - `hidden`: nothing is drawn.
 *  - `enabled`: the customer page with a valid server-issued link; `href` is
 *    exactly `appointmentManageUrl`'s value.
 *  - `preview`: the authoring preview. The action is drawn so the operator can
 *    see what `showManage` controls, but it is disabled and carries no address —
 *    a preview has no booking, and even one that does is not the customer's page. */
export type AppointmentManageAction =
  | { state: "hidden" }
  | { state: "enabled"; href: string }
  | { state: "preview"; reason: string };

/** The accessible explanation for the disabled preview action. */
export const MANAGE_PREVIEW_REASON =
  "Preview only. On the customer's confirmation this opens their own appointment page, using the secure link issued with their booking.";

/**
 * The manage action for one render of the confirmation. `mode` is the screen's
 * own mount mode: only `"public"` (the customer page) can yield an enabled
 * action, and only through `appointmentManageUrl`. In `"preview"` the result
 * never carries an address, whatever the response holds, so nothing in the
 * preview can navigate or expose a derived link.
 */
export function appointmentManageAction(
  response: unknown, showManage: unknown, kind: ConfirmationKind, mode: "public" | "preview",
): AppointmentManageAction {
  if ((kind !== "appointment" && kind !== "events" && kind !== "landing") || showManage === false) return { state: "hidden" };
  /* The disabled PREVIEW affordance is appointment-only (CC, 2026-09-23). It
     exists so an author can see what the appointment's `showManage` switch
     controls; the event and landing confirmations render only the `enabled`
     state, so returning `preview` for them would hand the UI a state it does
     not draw and would make `appointmentManageAction(null, true, "events",
     "preview")` answer something other than hidden. Nothing to preview without
     a registration, so: hidden. */
  if (kind !== "appointment") {
    /* The DISABLED PREVIEW arm reaches events and landing too (Pat / CC,
       2026-09-23). It was appointment-only because the event confirmation had
       no `showManage` switch, so there was nothing for a preview to explain.
       `0923a` gave it one, and an author flipping a switch should see what it
       controls. The event and landing confirmations now draw this state, so
       returning it no longer hands the UI something nothing renders. */
    if (mode !== "public") return { state: "preview", reason: MANAGE_PREVIEW_REASON };
    const eventHref = appointmentManageUrl(response, showManage, kind);
    return eventHref ? { state: "enabled", href: eventHref } : { state: "hidden" };
  }
  if (mode !== "public") return { state: "preview", reason: MANAGE_PREVIEW_REASON };
  const href = appointmentManageUrl(response, showManage, kind);
  return href ? { state: "enabled", href } : { state: "hidden" };
}

/**
 * Opens the customer's own manage page in a NEW browser tab, and never in the
 * current tab or frame. The confirmation often runs inside the One Stop popup's
 * iframe, where navigating the document replaced the journey inside the popup
 * with a page that has no way back to One Stop.
 *
 * The new tab gets no handle back to this page. `opener` is cleared on the
 * returned handle instead of passing a `noopener` feature, because the feature
 * form makes `window.open` return null even when the tab did open (the same
 * choice as the One Stop hand-off in blocks/one-stop/actions.tsx). If the browser
 * refuses the tab, nothing else happens: the confirmation email carries the
 * same link. Returns whether a tab opened.
 */
export function openManageInNewTab(href: string): boolean {
  if (typeof window === "undefined" || !href) return false;
  const opened = window.open(href, "_blank");
  if (!opened) return false;
  opened.opener = null;
  return true;
}

/** A bare loopback authority (optionally with a port): the one case where a
 *  plain-HTTP link can be the honest one, because a local generator serves the
 *  public page over http and hardcoding `https://` fails TLS against it. */
const LOOPBACK_AUTHORITY = /^(localhost|127\.0\.0\.1|\[::1\])(:\d+)?$/i;

/** The protocol the ACTUAL browser origin is served over, or "" when there is
 *  no browser (tests, SSR, workers). Never a guess: only `window.location`. */
function browserProtocol(): string {
  try {
    if (typeof window !== "undefined" && window.location && typeof window.location.protocol === "string") {
      return window.location.protocol.toLowerCase();
    }
  } catch (err) { /* non-browser context */ }
  return "";
}

/* ── The public address ─────────────────────────────────────────────────── */

/** `scheme://authority` for the journey's registered `domain`, or "" when the
 *  server has not given it one. Scheme rules, in order:
 *   1. a `domain` that already declares `http://` or `https://` keeps it;
 *   2. a bare loopback authority (`localhost`, `127.0.0.1`, `[::1]`, with or
 *      without a port) is `http` ONLY when the actual browser origin is http;
 *   3. everything else — real hosts, and any non-browser/default context — is
 *      `https`. */
export function publicOriginFor(journey: Dict | null | undefined): string {
  const domain = str(rec(journey).domain).trim();
  if (!domain) return "";
  const explicit = /^(https?:)\/\/(.*)$/i.exec(domain);
  const authority = (explicit ? explicit[2] : domain.replace(/^\/\//, "")).replace(/\/+$/, "");
  if (!authority) return "";
  let scheme = "https:";
  if (explicit) scheme = explicit[1].toLowerCase();
  else if (LOOPBACK_AUTHORITY.test(authority) && browserProtocol() === "http:") scheme = "http:";
  return scheme + "//" + authority;
}

/** An admin row's own declaration of its address, if the server made one.
 *  `publicUrl` wins and is kept VERBATIM apart from collapsing a run of
 *  trailing slashes to one (a bare origin becomes `origin/`); its scheme is the
 *  server's, never rewritten. Anything that is not `http(s)://…` is ignored —
 *  never an invented address. `publicPath` is resolved against `domain`. */
function declaredLink(j: Dict): string {
  const publicUrl = str(j.publicUrl).trim();
  if (publicUrl) {
    const explicit = /^(https?:)\/\/([^/]+)(\/.*)?$/i.exec(publicUrl);
    if (explicit) {
      const path = (explicit[3] || "/").replace(/\/+$/, "") || "/";
      return explicit[1].toLowerCase() + "//" + explicit[2] + path;
    }
  }
  const publicPath = str(j.publicPath).trim();
  if (publicPath) {
    const origin = publicOriginFor(j);
    if (origin) return origin + (publicPath.charAt(0) === "/" ? publicPath : "/" + publicPath);
  }
  return "";
}

/** The public link the server knows. `domain` is the branded host the composer
 *  registered for this tenant, so the tab never derives a hostname itself, and
 *  only declared contract fields are read — an absent one yields "" rather than
 *  an invented address. Resolution order:
 *   1. the row's own `publicUrl` (verbatim, scheme preserved), else its
 *      `publicPath` against `domain` — the server's declaration always wins,
 *      and it is the ONLY way a bare `scheme://domain/` (the booking root) is
 *      ever printed. The tab never asks which journey owns the root;
 *   2. `scheme://domain/{slug}` — every journey without a declaration.
 *  The internal slug is never touched: `loadPublishedJourney`,
 *  `loadPublicCatalog`, availability and booking keep keying on `journey.slug`
 *  whatever this function prints. */
export function publicLinkFor(journey: Dict | null | undefined): string {
  const j = rec(journey);
  const declared = declaredLink(j);
  if (declared) return declared;
  const origin = publicOriginFor(j);
  const slug = str(j.slug).trim();
  if (!origin || !slug) return "";
  return origin + "/" + slug;
}

/* ── Refusals ───────────────────────────────────────────────────────────── */

export interface RefusedField { path: string; message: string; control: ControlRef | null }
export interface AppointmentSaveRefusal {
  kind: "config-invalid" | "envelope-invalid" | "not-found" | "conflict" | "unavailable";
  code: string;
  status: number;
  message: string;
  fields: RefusedField[];
}
export type AppointmentSaveResult =
  | { ok: true; journey: Dict; noop?: boolean }
  | { ok: false; refusal: AppointmentSaveRefusal };

function refusalFrom(err: unknown): AppointmentSaveRefusal {
  const e = (err || {}) as Dict & { fields?: Array<Dict>; status?: number; code?: string; message?: string; name?: string };
  if (e.name === "ZodError") {
    return { kind: "unavailable", code: "RESPONSE_SHAPE", status: 0, fields: [],
      message: "The server answered in a shape this screen does not recognise; the save may or may not have landed. Reload to check." };
  }
  const status = Number(e.status || 0);
  const code = String(e.code || "UNKNOWN");
  const fields = Array.isArray(e.fields) ? e.fields : [];
  const kind: AppointmentSaveRefusal["kind"] =
    code === "JOURNEY_CONFIG_INVALID" || status === 422 ? "config-invalid"
      : code === "VALIDATION_ERROR" || status === 400 ? "envelope-invalid"
        : status === 404 ? "not-found"
          : status === 409 ? "conflict" : "unavailable";
  return {
    kind, code, status,
    message: String(e.message || "The server refused this save."),
    fields: fields.map((f) => {
      const path = String((f as Dict).path || "");
      return { path, message: String((f as Dict).message || ""), control: controlForPath(path) };
    }),
  };
}

/* ── The door ───────────────────────────────────────────────────────────── */

/** GET /v1/admin/journeys -> the tenant's appointment journey row, preferring the
 *  given slug. Returns the ROW (id, slug, domain, status…), not just an id.
 *
 *  `preferredId` is the tab's remembered pin. Matching it HERE, against the
 *  list, is what makes a stale pin harmless: the id is honoured while the
 *  journey it names is still listed, and ignored the moment it is not. The
 *  screen used to read the pinned id directly through `getAdminJourney`,
 *  which meant (a) a deleted journey's pin sent the tab down a 404 path, and
 *  (b) even a LIVE pin skipped the list, so the row's address fields
 *  (`domain`/`publicUrl`/`publicPath`) never reached `adoptJourney` and the
 *  public link came back empty (2026-09-14). */
export async function resolveAppointmentJourney(preferredSlug?: string, preferredId?: string): Promise<Dict | null> {
  const res = await listAdminJourneys();
  reportOpSource("listAdminJourneys", res);
  const rows = (res && Array.isArray(res.journeys) ? res.journeys : []) as Array<Dict>;
  const appt = rows.filter((r) => String(r.type || "") === "appointment");
  const byId = preferredId ? appt.filter((r) => String(r.id || "") === preferredId)[0] : null;
  const bySlug = preferredSlug ? appt.filter((r) => String(r.slug || "") === preferredSlug)[0] : null;
  return byId || bySlug || appt[0] || null;
}

/** The id-only door the current screen calls. Prefer `resolveAppointmentJourney`
 *  (the row) followed by `loadAppointmentJourney` (the config) for hydration. */
export async function resolveAppointmentJourneyId(preferredSlug?: string): Promise<string | null> {
  const row = await resolveAppointmentJourney(preferredSlug);
  return row ? String(row.id) : null;
}

/** GET /v1/admin/journeys/{id} — the full stored config, for hydration. */
export async function loadAppointmentJourney(journeyId: string): Promise<Dict> {
  const journey = (await getAdminJourney(journeyId)) as unknown as Dict;
  reportOpSource("getAdminJourney", journey);
  return journey;
}

/** POST /v1/admin/journeys — an appointment journey from the 'classic' starter,
 *  for a tenant that has none. */
export async function createAppointmentJourney(name: string): Promise<AppointmentSaveResult> {
  try {
    const journey = (await createAdminJourney({ templateId: "classic", name, type: "appointment" })) as unknown as Dict;
    reportOpSource("createAdminJourney", journey);
    return { ok: true, journey };
  } catch (err) {
    return { ok: false, refusal: refusalFrom(err) };
  }
}

/**
 * PATCH /v1/admin/journeys/{id} with a whitelist-only, changed-leaves-only body.
 * An empty diff is a no-op: nothing is sent and the stored journey is returned.
 */
export async function saveAppointmentSettings(
  journeyId: string, s: Dict, ct: Dict = {}, opts: PatchOptions = {},
): Promise<AppointmentSaveResult> {
  const body = buildAppointmentPatch(s, ct, opts);
  try {
    if (Object.keys(body).length === 0) {
      const current = opts.journey || (await loadAppointmentJourney(journeyId));
      return { ok: true, journey: current, noop: true };
    }
    const journey = (await updateAdminJourney(journeyId, body as Parameters<typeof updateAdminJourney>[1])) as unknown as Dict;
    reportOpSource("updateAdminJourney", journey);
    return { ok: true, journey };
  } catch (err) {
    return { ok: false, refusal: refusalFrom(err) };
  }
}

/** POST /v1/admin/journeys/{id}/status — publish is a status op, not a second write. */
export async function publishAppointmentJourney(journeyId: string): Promise<AppointmentSaveResult> {
  try {
    const journey = (await setAdminJourneyStatus(journeyId, { status: "published" })) as unknown as Dict;
    reportOpSource("setAdminJourneyStatus", journey);
    return { ok: true, journey };
  } catch (err) {
    return { ok: false, refusal: refusalFrom(err) };
  }
}
